import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  User,
  Star,
  ExternalLink,
  Mail,
  Phone,
  MessageCircle,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Image,
  Edit2,
  Save,
  DollarSign,
  Trash2,
  ChevronDown,
  ChevronRight,
  Link,
  Briefcase,
  ClipboardList,
  MessageSquare,
  HelpCircle,
  Upload,
  ImagePlus,
  X,
  ZoomIn,
} from "lucide-react";

const STAGES = [
  { id: "new", label: "New", color: "bg-zinc-500" },
  { id: "portfolio_review", label: "Portfolio Review", color: "bg-blue-500" },
  { id: "potential_fit", label: "Potential Fit", color: "bg-cyan-500" },
  { id: "test_assigned", label: "Test Assigned", color: "bg-yellow-500" },
  { id: "test_review", label: "Test Review", color: "bg-orange-500" },
  { id: "negotiation", label: "Negotiation", color: "bg-purple-500" },
  { id: "approved", label: "Approved", color: "bg-green-500" },
  { id: "rejected", label: "Rejected", color: "bg-red-500" },
  { id: "second_chance", label: "Second Chance", color: "bg-amber-500" },
];

const REJECTION_REASONS = [
  { id: "portfolio_quality", label: "Portfolio Quality" },
  { id: "style_mismatch", label: "Style Mismatch" },
  { id: "communication", label: "Communication Issues" },
  { id: "rate_too_high", label: "Rate Too High" },
  { id: "slow_delivery", label: "Slow Delivery" },
  { id: "other", label: "Other" },
];

// Service Rate Row - always visible inputs with tooltip description
const ServiceRateRow = ({ service, onSave, existingRate }) => {
  const [rate, setRate] = useState(existingRate?.rate?.toString() || "");
  const [hours, setHours] = useState(existingRate?.hours?.toString() || "");
  const [isSaving, setIsSaving] = useState(false);
  
  const handleSave = async () => {
    if (rate) {
      setIsSaving(true);
      await onSave(parseFloat(rate), hours ? parseFloat(hours) : null);
      setIsSaving(false);
    }
  };

  const hasChanges = existingRate 
    ? (rate !== existingRate.rate?.toString() || hours !== (existingRate.hours?.toString() || ""))
    : rate !== "";

  return (
    <TooltipProvider>
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800 last:border-b-0 hover:bg-zinc-800/30">
        {/* Service name with tooltip */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 cursor-help">
                <HelpCircle className="h-3 w-3 text-zinc-500 flex-shrink-0" />
                <span className="text-sm text-zinc-300">{service.name}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-zinc-900 border-zinc-700 max-w-xs p-3">
              {service.example_image && (
                <img src={service.example_image} alt={service.name} className="w-full h-24 object-cover rounded mb-2" />
              )}
              <p className="text-xs font-medium text-zinc-50 mb-1">{service.name}</p>
              <p className="text-xs text-zinc-400">{service.description || "No description available"}</p>
              {service.base_price && (
                <p className="text-xs text-zinc-500 mt-1">Base price: ${service.base_price}</p>
              )}
            </TooltipContent>
          </Tooltip>
          {service.is_sq_ft_pricing && (
            <span className="text-[9px] text-zinc-500 bg-zinc-800 px-1 rounded flex-shrink-0">sq.ft</span>
          )}
        </div>
        
        {/* Always visible inputs */}
        <div className="flex items-center gap-1">
          <Input
            type="number"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onBlur={() => hasChanges && handleSave()}
            placeholder="$"
            className={cn(
              "w-20 h-7 text-xs border-zinc-700 text-zinc-50",
              existingRate ? "bg-green-900/20 border-green-700/50" : "bg-zinc-950"
            )}
          />
          <Input
            type="number"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            onBlur={() => hasChanges && handleSave()}
            placeholder="hrs"
            className={cn(
              "w-16 h-7 text-xs border-zinc-700 text-zinc-50",
              existingRate ? "bg-green-900/20 border-green-700/50" : "bg-zinc-950"
            )}
          />
          {hasChanges && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSave}
              disabled={isSaving || !rate}
              className="h-7 w-7 p-0 text-green-500 hover:text-green-400"
            >
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

// Compact Section Component - shows minimal UI when empty
const Section = ({ title, icon: Icon, count, isEmpty, children, onAdd, addLabel = "Add" }) => {
  const [isOpen, setIsOpen] = useState(!isEmpty);
  
  return (
    <Card className="bg-zinc-900/50 border-zinc-800">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-zinc-800/30 transition-colors py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-500" /> : <ChevronRight className="h-4 w-4 text-zinc-500" />}
                <Icon className="h-4 w-4 text-orange-500" />
                <CardTitle className="text-sm text-zinc-50">{title}</CardTitle>
                {count !== undefined && (
                  <Badge variant="secondary" className="bg-zinc-800 text-zinc-400 text-xs">
                    {count}
                  </Badge>
                )}
              </div>
              {onAdd && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); onAdd(); }}
                  className="h-7 text-xs text-orange-500 hover:text-orange-400 hover:bg-orange-500/10"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {addLabel}
                </Button>
              )}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4">
            {children}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

const CandidateDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceTypes, setServiceTypes] = useState([]);
  
  // Dialog states
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [testTaskDialogOpen, setTestTaskDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [deletingImage, setDeletingImage] = useState(null);
  
  // Form states
  const [noteForm, setNoteForm] = useState({
    note: "",
    quality_rating: "",
    style_compatibility: "",
    technical_skill: "",
  });
  
  const [testTaskForm, setTestTaskForm] = useState({
    title: "",
    description: "",
    deadline: "",
  });
  
  const [rejectForm, setRejectForm] = useState({
    reason: "",
    notes: "",
    can_retry: false,
  });

  const [rateForm, setRateForm] = useState({
    service_type_id: "",
    rate: "",
    currency: "USD",
    notes: "",
  });

  const [portfolioForm, setPortfolioForm] = useState({
    url: "",
  });

  const [editForm, setEditForm] = useState({});

  const fetchCandidate = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/candidates/${id}`);
      setCandidate(res.data);
      setEditForm({
        name: res.data.name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        telegram: res.data.telegram || "",
        internal_notes: res.data.candidate_profile?.internal_notes || "",
        experience_level: res.data.candidate_profile?.experience_level || "",
        availability: res.data.candidate_profile?.availability || "",
        communication_quality: res.data.candidate_profile?.communication_quality || "",
      });
    } catch (error) {
      toast.error("Failed to load candidate");
      navigate("/candidates");
    } finally {
      setLoading(false);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get("/settings/service-types");
      setServiceTypes(res.data || []);
    } catch (error) {
      console.error("Failed to load service types", error);
    }
  };

  useEffect(() => {
    fetchCandidate();
    fetchServiceTypes();
  }, [id]);

  const handleStageChange = async (newStage) => {
    try {
      await api.put(`/candidates/${id}/stage?stage=${newStage}`);
      toast.success("Stage updated");
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  const handleAddNote = async () => {
    if (!noteForm.note.trim()) {
      toast.error("Note is required");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("note", noteForm.note);
      if (noteForm.quality_rating) params.append("quality_rating", noteForm.quality_rating);
      if (noteForm.style_compatibility) params.append("style_compatibility", noteForm.style_compatibility);
      if (noteForm.technical_skill) params.append("technical_skill", noteForm.technical_skill);

      await api.post(`/candidates/${id}/notes?${params.toString()}`);
      toast.success("Note added");
      setNoteDialogOpen(false);
      setNoteForm({ note: "", quality_rating: "", style_compatibility: "", technical_skill: "" });
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const handleCreateTestTask = async () => {
    if (!testTaskForm.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("title", testTaskForm.title);
      if (testTaskForm.description) params.append("description", testTaskForm.description);
      if (testTaskForm.deadline) params.append("deadline", testTaskForm.deadline);

      await api.post(`/candidates/${id}/test-task?${params.toString()}`);
      toast.success("Test task created");
      setTestTaskDialogOpen(false);
      setTestTaskForm({ title: "", description: "", deadline: "" });
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to create test task");
    }
  };

  const handleApprove = async () => {
    try {
      await api.post(`/candidates/${id}/approve`);
      toast.success("Candidate approved and converted to active artist!");
      setApproveDialogOpen(false);
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to approve candidate");
    }
  };

  const handleReject = async () => {
    if (!rejectForm.reason) {
      toast.error("Rejection reason is required");
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("reason", rejectForm.reason);
      if (rejectForm.notes) params.append("notes", rejectForm.notes);
      if (rejectForm.can_retry) params.append("can_retry", "true");

      await api.post(`/candidates/${id}/reject?${params.toString()}`);
      toast.success("Candidate rejected");
      setRejectDialogOpen(false);
      setRejectForm({ reason: "", notes: "", can_retry: false });
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to reject candidate");
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/candidates/${id}`, editForm);
      toast.success("Changes saved");
      setEditMode(false);
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to save changes");
    }
  };

  const handleAddRate = async () => {
    if (!rateForm.service_type_id || !rateForm.rate) {
      toast.error("Service type and rate are required");
      return;
    }

    try {
      const selectedService = serviceTypes.find(s => s.id === rateForm.service_type_id);
      const newRate = {
        service_type_id: rateForm.service_type_id,
        service_type_name: selectedService?.name || "Unknown",
        rate: parseFloat(rateForm.rate),
        currency: rateForm.currency,
        is_sq_ft: selectedService?.is_sq_ft_pricing || false,
        notes: rateForm.notes || null,
      };

      const existingRates = profile.service_rates || [];
      const updatedRates = existingRates.filter(r => r.service_type_id !== rateForm.service_type_id);
      updatedRates.push(newRate);

      await api.put(`/candidates/${id}`, { service_rates: updatedRates });
      toast.success("Rate added");
      setRateDialogOpen(false);
      setRateForm({ service_type_id: "", rate: "", currency: "USD", notes: "" });
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to add rate");
    }
  };

  const handleDeleteRate = async (serviceTypeId) => {
    try {
      const existingRates = profile.service_rates || [];
      const updatedRates = existingRates.filter(r => r.service_type_id !== serviceTypeId);
      
      await api.put(`/candidates/${id}`, { service_rates: updatedRates });
      toast.success("Rate removed");
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to remove rate");
    }
  };

  // Inline add rate from service table
  const handleAddRateInline = async (service, rate, hours) => {
    try {
      const newRate = {
        service_type_id: service.id,
        service_type_name: service.name,
        rate: rate,
        currency: "USD",
        is_sq_ft: service.is_sq_ft_pricing || false,
        hours: hours,
      };

      const existingRates = profile.service_rates || [];
      const updatedRates = [...existingRates.filter(r => r.service_type_id !== service.id), newRate];

      await api.put(`/candidates/${id}`, { service_rates: updatedRates });
      toast.success(`${service.name}: $${rate}${hours ? ` / ${hours}h` : ""}`);
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to add rate");
    }
  };

  const handleAddPortfolioLink = async () => {
    if (!portfolioForm.url.trim()) {
      toast.error("URL is required");
      return;
    }

    try {
      const existingLinks = profile.portfolio_links || [];
      const updatedLinks = [...existingLinks, portfolioForm.url.trim()];
      
      await api.put(`/candidates/${id}`, { portfolio_links: updatedLinks });
      toast.success("Portfolio link added");
      setPortfolioDialogOpen(false);
      setPortfolioForm({ url: "" });
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to add link");
    }
  };

  const handleDeletePortfolioLink = async (linkToDelete) => {
    try {
      const existingLinks = profile.portfolio_links || [];
      const updatedLinks = existingLinks.filter(l => l !== linkToDelete);
      
      await api.put(`/candidates/${id}`, { portfolio_links: updatedLinks });
      toast.success("Link removed");
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to remove link");
    }
  };

  const handleUploadHeroImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, WebP, GIF");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size: 10MB");
      return;
    }

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await api.post(`/candidates/${id}/hero-image`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Hero image uploaded");
      fetchCandidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSetHeroImage = async (imageUrl) => {
    try {
      await api.put(`/candidates/${id}/hero-image?image_url=${encodeURIComponent(imageUrl)}`);
      toast.success("Hero image updated");
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to set hero image");
    }
  };

  // Handle paste from clipboard
  const handlePasteImage = async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        try {
          setUploadingImage(true);
          const formData = new FormData();
          formData.append("file", file, `clipboard_${Date.now()}.png`);

          await api.post(`/candidates/${id}/hero-image`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });

          toast.success("Image pasted and uploaded");
          fetchCandidate();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Failed to upload pasted image");
        } finally {
          setUploadingImage(false);
        }
        break;
      }
    }
  };

  // Handle delete work sample
  const handleDeleteWorkSample = async (imageUrl) => {
    try {
      setDeletingImage(imageUrl);
      await api.delete(`/candidates/${id}/work-sample?image_url=${encodeURIComponent(imageUrl)}`);
      toast.success("Image deleted");
      fetchCandidate();
    } catch (error) {
      toast.error("Failed to delete image");
    } finally {
      setDeletingImage(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!candidate) return null;

  const profile = candidate.candidate_profile || {};
  const portfolioLinks = profile.portfolio_links || [];
  const serviceRates = profile.service_rates || [];
  const testTasks = candidate.test_tasks || [];
  const notes = candidate.evaluation_history || [];
  const stageConfig = STAGES.find(s => s.id === candidate.candidate_stage) || STAGES[0];

  // Get portfolio link type
  const getLinkType = (url) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes("artstation")) return { icon: "🎨", label: "ArtStation" };
    if (lowerUrl.includes("behance")) return { icon: "🅱️", label: "Behance" };
    if (lowerUrl.includes("instagram")) return { icon: "📷", label: "Instagram" };
    if (lowerUrl.includes("drive.google")) return { icon: "📁", label: "Google Drive" };
    if (lowerUrl.includes("dropbox")) return { icon: "📦", label: "Dropbox" };
    return { icon: "🔗", label: "Link" };
  };

  return (
    <div className="space-y-4 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/candidates")}
            className="text-zinc-400 hover:text-zinc-50"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center">
              <User className="h-6 w-6 text-zinc-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-zinc-50">{candidate.name}</h1>
              <p className="text-xs text-zinc-500">
                Added {candidate.created_at ? new Date(candidate.created_at).toLocaleDateString() : "—"}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Stage Selector */}
          <Select value={candidate.candidate_stage} onValueChange={handleStageChange}>
            <SelectTrigger className={cn("w-[160px] h-9 text-sm border-0", stageConfig.color, "text-zinc-50")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {STAGES.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-zinc-50">{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setRejectDialogOpen(true)}
            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
          >
            <XCircle className="h-4 w-4 mr-1" />
            Reject
          </Button>
          <Button
            size="sm"
            onClick={() => setApproveDialogOpen(true)}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            Approve
          </Button>
        </div>
      </div>

      {/* Work Samples Section - Clean Grid */}
      <Card 
        className="bg-zinc-900/50 border-zinc-800"
        onPaste={handlePasteImage}
        tabIndex={0}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-zinc-400 flex items-center gap-2">
              <Image className="h-4 w-4" />
              Work Samples
              {(profile.work_samples || []).length > 0 && (
                <Badge variant="secondary" className="bg-zinc-800 text-zinc-400 text-xs">
                  {(profile.work_samples || []).length}
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-zinc-500">Ctrl+V для вставки</span>
              <label className="cursor-pointer">
                <Button size="sm" variant="ghost" className="h-7 text-xs text-orange-500 hover:text-orange-400" asChild>
                  <span>
                    <Upload className="h-3 w-3 mr-1" />
                    Загрузить
                  </span>
                </Button>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleUploadHeroImage}
                  className="hidden"
                  disabled={uploadingImage}
                  multiple
                />
              </label>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {(profile.work_samples || []).length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {(profile.work_samples || []).map((url, i) => {
                const isHero = (profile.hero_image || profile.work_samples?.[0]) === url;
                const isDeleting = deletingImage === url;
                
                return (
                  <div 
                    key={i} 
                    className={cn(
                      "relative aspect-square rounded-lg overflow-hidden bg-zinc-800 group",
                      isHero && "ring-2 ring-orange-500",
                      isDeleting && "opacity-50"
                    )}
                  >
                    {/* Thumbnail image with error handling */}
                    <img 
                      src={url} 
                      alt={`Work sample ${i + 1}`} 
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    {/* Fallback placeholder (hidden by default) */}
                    <div className="absolute inset-0 bg-zinc-700 items-center justify-center hidden">
                      <div className="text-center">
                        <User className="h-8 w-8 text-zinc-500 mx-auto mb-1" />
                        <span className="text-[9px] text-zinc-500">Image unavailable</span>
                      </div>
                    </div>
                    
                    {/* Hero badge */}
                    {isHero && (
                      <div className="absolute top-1 left-1 bg-orange-500 text-white text-[9px] px-1.5 py-0.5 rounded font-medium z-10">
                        HERO
                      </div>
                    )}
                    
                    {/* Hover overlay with actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                      {/* Zoom button */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setLightboxImage(url)}
                        className="h-8 w-8 p-0 rounded-full bg-white/20 text-zinc-50 hover:bg-white/30"
                      >
                        <ZoomIn className="h-4 w-4" />
                      </Button>
                      
                      {/* Action buttons row */}
                      <div className="flex items-center gap-1">
                        {/* Set as Hero */}
                        {!isHero && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetHeroImage(url)}
                            className="h-7 px-2 text-[10px] bg-white/20 text-white hover:bg-orange-500"
                          >
                            <Star className="h-3 w-3 mr-1" />
                            Hero
                          </Button>
                        )}
                        
                        {/* Delete */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteWorkSample(url)}
                          disabled={isDeleting}
                          className="h-7 px-2 text-[10px] bg-white/20 text-white hover:bg-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {/* Upload placeholder */}
              <label className="aspect-square rounded-lg border-2 border-dashed border-zinc-700 hover:border-zinc-500 flex flex-col items-center justify-center cursor-pointer transition-colors bg-zinc-800/30">
                {uploadingImage ? (
                  <div className="text-center">
                    <div className="h-6 w-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-1" />
                    <span className="text-[10px] text-zinc-500">Загрузка...</span>
                  </div>
                ) : (
                  <>
                    <Plus className="h-6 w-6 text-zinc-600 mb-1" />
                    <span className="text-[10px] text-zinc-600">Добавить</span>
                  </>
                )}
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleUploadHeroImage}
                  className="hidden"
                  disabled={uploadingImage}
                />
              </label>
            </div>
          ) : (
            <label className="block w-full py-12 border-2 border-dashed border-zinc-700 hover:border-zinc-500 rounded-lg cursor-pointer transition-colors">
              <div className="text-center">
                {uploadingImage ? (
                  <>
                    <div className="h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <span className="text-sm text-zinc-500">Загрузка...</span>
                  </>
                ) : (
                  <>
                    <ImagePlus className="h-10 w-10 text-zinc-600 mx-auto mb-2" />
                    <p className="text-sm text-zinc-500 mb-1">Нажмите для загрузки или Ctrl+V</p>
                    <p className="text-[10px] text-zinc-600">PNG, JPG, WebP до 10MB</p>
                  </>
                )}
              </div>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleUploadHeroImage}
                className="hidden"
                disabled={uploadingImage}
                multiple
              />
            </label>
          )}
        </CardContent>
      </Card>

      {/* Lightbox Dialog */}
      <Dialog open={!!lightboxImage} onOpenChange={() => setLightboxImage(null)}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-800 p-0">
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLightboxImage(null)}
              className="absolute top-2 right-2 h-8 w-8 p-0 rounded-full bg-black/50 text-zinc-50 hover:bg-black/70 z-10"
            >
              <X className="h-4 w-4" />
            </Button>
            {lightboxImage && (
              <img 
                src={lightboxImage} 
                alt="Full size" 
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Main Content - Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Profile Section */}
          <Section 
            title="Profile" 
            icon={User} 
            isEmpty={false}
            onAdd={editMode ? null : () => setEditMode(true)}
            addLabel={editMode ? null : "Edit"}
          >
            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Name</Label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Email</Label>
                    <Input
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                      className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Phone</Label>
                    <Input
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Telegram</Label>
                    <Input
                      value={editForm.telegram}
                      onChange={(e) => setEditForm({ ...editForm, telegram: e.target.value })}
                      className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Internal Notes</Label>
                  <Textarea
                    value={editForm.internal_notes}
                    onChange={(e) => setEditForm({ ...editForm, internal_notes: e.target.value })}
                    className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 min-h-[60px]"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setEditMode(false)} className="border-zinc-700 h-8">
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleSaveEdit} className="bg-orange-500 hover:bg-orange-600 h-8">
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 text-zinc-500" />
                  <span className="text-zinc-400">{candidate.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-3 w-3 text-zinc-500" />
                  <span className="text-zinc-400">{candidate.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-3 w-3 text-zinc-500" />
                  <span className="text-zinc-400">{candidate.telegram || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-3 w-3 text-zinc-500" />
                  <span className="text-zinc-400">Rating: {profile.skill_rating || "—"}/10</span>
                </div>
                {profile.internal_notes && (
                  <div className="col-span-2 mt-2 p-2 bg-zinc-800/50 rounded text-xs text-zinc-400 italic">
                    "{profile.internal_notes}"
                  </div>
                )}
              </div>
            )}
          </Section>

          {/* Service Rates Section - All services visible with inputs */}
          <Section 
            title="Service Rates" 
            icon={DollarSign} 
            count={serviceRates.length}
            isEmpty={false}
          >
            <div className="space-y-1">
              <div className="flex items-center justify-between px-3 py-1 text-xs text-zinc-500 bg-zinc-800/50 rounded-t">
                <span className="flex items-center gap-2">
                  <HelpCircle className="h-3 w-3" />
                  Hover service name for description
                </span>
                <span>{serviceRates.length}/{serviceTypes.length} defined</span>
              </div>
              <div className="max-h-[400px] overflow-y-auto border border-zinc-700 rounded-b-lg">
                {serviceTypes.map((service) => {
                  const existingRate = serviceRates.find(r => r.service_type_id === service.id);
                  return (
                    <ServiceRateRow 
                      key={service.id} 
                      service={service}
                      existingRate={existingRate}
                      onSave={(rate, hours) => handleAddRateInline(service, rate, hours)}
                    />
                  );
                })}
              </div>
            </div>
          </Section>

          {/* Portfolio Section */}
          <Section 
            title="Portfolio" 
            icon={Link} 
            count={portfolioLinks.length}
            isEmpty={portfolioLinks.length === 0}
            onAdd={() => setPortfolioDialogOpen(true)}
            addLabel="Add Link"
          >
            {portfolioLinks.length === 0 ? (
              <p className="text-zinc-500 text-xs text-center py-2">No portfolio links</p>
            ) : (
              <div className="space-y-1">
                {portfolioLinks.map((link, i) => {
                  const linkType = getLinkType(link);
                  return (
                    <div key={i} className="flex items-center justify-between p-2 bg-zinc-800/50 rounded text-sm group">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-lg">{linkType.icon}</span>
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 truncate"
                        >
                          {linkType.label}: {link.replace(/https?:\/\/(www\.)?/, '').substring(0, 40)}...
                        </a>
                        <ExternalLink className="h-3 w-3 text-zinc-500 flex-shrink-0" />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePortfolioLink(link)}
                        className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/20 opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Test Tasks Section */}
          <Section 
            title="Test Tasks" 
            icon={ClipboardList} 
            count={testTasks.length}
            isEmpty={testTasks.length === 0}
            onAdd={() => setTestTaskDialogOpen(true)}
            addLabel="Assign Test"
          >
            {testTasks.length === 0 ? (
              <p className="text-zinc-500 text-xs text-center py-2">No test tasks assigned</p>
            ) : (
              <div className="space-y-2">
                {testTasks.map((task, i) => (
                  <div key={i} className="p-3 bg-zinc-800/50 rounded">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-zinc-50 font-medium text-sm">{task.title}</span>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-xs",
                          task.status === "completed" ? "border-green-500 text-green-400" :
                          task.status === "in_progress" ? "border-yellow-500 text-yellow-400" :
                          "border-zinc-500 text-zinc-400"
                        )}
                      >
                        {task.status || "pending"}
                      </Badge>
                    </div>
                    {task.description && (
                      <p className="text-xs text-zinc-500">{task.description}</p>
                    )}
                    {task.deadline && (
                      <p className="text-xs text-zinc-500 mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        Deadline: {new Date(task.deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Notes Section */}
          <Section 
            title="Evaluation Notes" 
            icon={MessageSquare} 
            count={notes.length}
            isEmpty={notes.length === 0}
            onAdd={() => setNoteDialogOpen(true)}
            addLabel="Add Note"
          >
            {notes.length === 0 ? (
              <p className="text-zinc-500 text-xs text-center py-2">No evaluation notes</p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {notes.slice().reverse().map((note, i) => (
                  <div key={i} className="p-3 bg-zinc-800/50 rounded">
                    <p className="text-sm text-zinc-300">{note.notes || note.note || "—"}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                      {note.quality_rating && (
                        <span>Quality: <span className="text-yellow-400">{note.quality_rating}/10</span></span>
                      )}
                      {note.technical_skill && (
                        <span>Technical: <span className="text-blue-400">{note.technical_skill}/10</span></span>
                      )}
                      {note.style_compatibility && (
                        <span>Style: <span className="text-purple-400">{note.style_compatibility}/10</span></span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-600 mt-1">
                      {note.created_at ? new Date(note.created_at).toLocaleDateString() : "—"} by {note.author_name || "Unknown"}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {/* DIALOGS */}
      
      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Add Evaluation Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Note *</Label>
              <Textarea
                value={noteForm.note}
                onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })}
                placeholder="Your observations about this candidate..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 min-h-[80px]"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Quality (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={noteForm.quality_rating}
                  onChange={(e) => setNoteForm({ ...noteForm, quality_rating: e.target.value })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Technical (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={noteForm.technical_skill}
                  onChange={(e) => setNoteForm({ ...noteForm, technical_skill: e.target.value })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Style (1-10)</Label>
                <Input
                  type="number"
                  min="1"
                  max="10"
                  value={noteForm.style_compatibility}
                  onChange={(e) => setNoteForm({ ...noteForm, style_compatibility: e.target.value })}
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 h-8"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAddNote} className="bg-orange-500 hover:bg-orange-600">Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Test Task Dialog */}
      <Dialog open={testTaskDialogOpen} onOpenChange={setTestTaskDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Assign Test Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Task Title *</Label>
              <Input
                value={testTaskForm.title}
                onChange={(e) => setTestTaskForm({ ...testTaskForm, title: e.target.value })}
                placeholder="e.g., Interior Render Test"
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Description</Label>
              <Textarea
                value={testTaskForm.description}
                onChange={(e) => setTestTaskForm({ ...testTaskForm, description: e.target.value })}
                placeholder="Detailed requirements..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Deadline</Label>
              <Input
                type="date"
                value={testTaskForm.deadline}
                onChange={(e) => setTestTaskForm({ ...testTaskForm, deadline: e.target.value })}
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setTestTaskDialogOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleCreateTestTask} className="bg-orange-500 hover:bg-orange-600">Assign Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Approve Candidate</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400 mt-2">
            This will convert <span className="text-zinc-50 font-medium">{candidate.name}</span> to an active artist.
            They will be able to receive task assignments.
          </p>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleApprove} className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-4 w-4 mr-1" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Reject Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Reason *</Label>
              <Select value={rejectForm.reason} onValueChange={(v) => setRejectForm({ ...rejectForm, reason: v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {REJECTION_REASONS.map(r => (
                    <SelectItem key={r.id} value={r.id} className="text-zinc-50">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Notes</Label>
              <Textarea
                value={rejectForm.notes}
                onChange={(e) => setRejectForm({ ...rejectForm, notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="can_retry"
                checked={rejectForm.can_retry}
                onChange={(e) => setRejectForm({ ...rejectForm, can_retry: e.target.checked })}
                className="rounded border-zinc-700"
              />
              <Label htmlFor="can_retry" className="text-zinc-400 text-xs">Allow second chance (can reapply later)</Label>
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleReject} className="bg-red-600 hover:bg-red-700">
              <XCircle className="h-4 w-4 mr-1" />
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Service Rate Dialog */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Add Service Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">Service Type *</Label>
              <Select
                value={rateForm.service_type_id}
                onValueChange={(v) => setRateForm({ ...rateForm, service_type_id: v })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1">
                  <SelectValue placeholder="Select service type..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[300px]">
                  {serviceTypes.length === 0 ? (
                    <SelectItem value="_loading" disabled className="text-zinc-500">Loading services...</SelectItem>
                  ) : (
                    serviceTypes.map(s => (
                      <SelectItem key={s.id} value={s.id} className="text-zinc-50">
                        {s.name}
                        {s.is_sq_ft_pricing && " (per sq.ft)"}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs">Expected Rate *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={rateForm.rate}
                  onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })}
                  placeholder="0.00"
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Currency</Label>
                <Select
                  value={rateForm.currency}
                  onValueChange={(v) => setRateForm({ ...rateForm, currency: v })}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="USD" className="text-zinc-50">USD ($)</SelectItem>
                    <SelectItem value="EUR" className="text-zinc-50">EUR (€)</SelectItem>
                    <SelectItem value="UAH" className="text-zinc-50">UAH (₴)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Notes</Label>
              <Input
                value={rateForm.notes}
                onChange={(e) => setRateForm({ ...rateForm, notes: e.target.value })}
                placeholder="Any notes about this rate..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setRateDialogOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAddRate} className="bg-orange-500 hover:bg-orange-600">
              <DollarSign className="h-4 w-4 mr-1" />
              Add Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Portfolio Link Dialog */}
      <Dialog open={portfolioDialogOpen} onOpenChange={setPortfolioDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Add Portfolio Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs">URL *</Label>
              <Input
                value={portfolioForm.url}
                onChange={(e) => setPortfolioForm({ ...portfolioForm, url: e.target.value })}
                placeholder="https://artstation.com/..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setPortfolioDialogOpen(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={handleAddPortfolioLink} className="bg-orange-500 hover:bg-orange-600">
              <Plus className="h-4 w-4 mr-1" />
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CandidateDetailPage;
